// client/src/hooks/useOfertas.js (VERSÃO COMPLETA CORRIGIDA)
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';
import useAlert from './useAlert';

const OFERTAS_QUERY_KEY = 'ofertas';

const fetchOfertas = async (params = {}) => {
    try {
        console.log('🔄 [useOfertas] Fazendo requisição com params:', params);
        const { data } = await apiClient.get('/ofertas', { params });
        console.log('✅ [useOfertas] Sucesso:', data.data?.length, 'ofertas');
        return data;
    } catch (error) {
        console.error('❌ [useOfertas] Erro:', {
            status: error.response?.status,
            data: error.response?.data,
            message: error.message
        });
        
        const errorMessage = error.response?.data?.error || 
                           error.response?.data?.message || 
                           error.message || 
                           'Erro ao carregar ofertas';
        
        throw new Error(`Falha: ${errorMessage} (Status: ${error.response?.status || 'N/A'})`);
    }
};

const fetchOferta = async (ofertaId) => {
    const { data } = await apiClient.get(`/ofertas/${ofertaId}`);
    return data.data;
};

const createOfertaFn = (ofertaData) => apiClient.post('/ofertas', ofertaData);
const updateOfertaFn = ({ id, ofertaData }) => apiClient.put(`/ofertas/${id}`, ofertaData);
const deleteOfertaFn = (id) => apiClient.delete(`/ofertas/${id}`);

const uploadOfertaImagemFn = ({ ofertaId, file }) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/ofertas/${ofertaId}/imagens`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

const deleteOfertaImagemFn = ({ ofertaId, imageId }) => {
    return apiClient.delete(`/ofertas/${ofertaId}/imagens/${imageId}`);
};

const setOfertaImagemPrincipalFn = ({ ofertaId, tipo, refId }) => {
    return apiClient.patch(`/ofertas/${ofertaId}/imagens/principal`, { tipo, refId });
};

export const useOferta = (ofertaId) => {
    return useQuery({
        queryKey: [OFERTAS_QUERY_KEY, ofertaId],
        queryFn: () => fetchOferta(ofertaId),
        enabled: !!ofertaId,
    });
};

export const useOfertas = (params = {}, options = {}) => {
    return useQuery({
        queryKey: [OFERTAS_QUERY_KEY, params],
        queryFn: () => fetchOfertas(params),
        retry: (failureCount, error) => {
            if (error.message.includes('500') || failureCount >= 2) {
                return false;
            }
            return true;
        },
        ...options,
    });
};

export const useOfertasFiltradas = (filtros = {}) => {
    return useOfertas(filtros);
};

export const useOfertasAtivas = (filtrosAdicionais = {}) => {
    return useOfertasFiltradas({ ativo: true, ...filtrosAdicionais });
};

export const useOfertasPorTipo = (tipo, filtrosAdicionais = {}) => {
    return useOfertasFiltradas({ tipo, ...filtrosAdicionais });
};

export const useMutateOfertas = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidateCache = (ofertaId = null) => {
        queryClient.invalidateQueries({ queryKey: [OFERTAS_QUERY_KEY] });
        if (ofertaId) {
            queryClient.invalidateQueries({ queryKey: [OFERTAS_QUERY_KEY, ofertaId] });
        }
    };

    const createOferta = useMutation({
        mutationFn: createOfertaFn,
        onSuccess: (response) => {
            invalidateCache();
            setAlert(response.data.message || 'Oferta criada com sucesso!', 'success');
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao criar oferta', 'danger'),
    });

    const updateOferta = useMutation({
        mutationFn: updateOfertaFn,
        onSuccess: (response, variables) => {
            invalidateCache(variables.id);
            setAlert(response.data.message || 'Oferta atualizada com sucesso!', 'success');
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao atualizar oferta', 'danger'),
    });

    const deleteOferta = useMutation({
        mutationFn: deleteOfertaFn,
        onSuccess: (response) => {
            invalidateCache();
            setAlert(response.data.message || 'Oferta removida com sucesso!', 'success');
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao remover oferta', 'danger'),
    });

    const uploadOfertaImagem = useMutation({
        mutationFn: uploadOfertaImagemFn,
        onSuccess: (response, variables) => {
            invalidateCache(variables.ofertaId);
            setAlert(response.data.message || 'Imagem enviada com sucesso!', 'success');
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao enviar imagem', 'danger'),
    });

    const deleteOfertaImagem = useMutation({
        mutationFn: deleteOfertaImagemFn,
        onSuccess: (response, variables) => {
            invalidateCache(variables.ofertaId);
            setAlert(response.data.message || 'Imagem removida com sucesso!', 'success');
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao remover imagem', 'danger'),
    });
    
    const setOfertaImagemPrincipal = useMutation({
        mutationFn: setOfertaImagemPrincipalFn,
        onSuccess: (response, variables) => {
            invalidateCache(variables.ofertaId);
            setAlert(response.data.message || 'Imagem principal definida!', 'success');
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao definir imagem principal', 'danger'),
    });
    
    const isPending = createOferta.isPending || 
                      updateOferta.isPending || 
                      deleteOferta.isPending || 
                      uploadOfertaImagem.isPending ||
                      deleteOfertaImagem.isPending ||
                      setOfertaImagemPrincipal.isPending;

    return {
        createOferta,
        updateOferta,
        deleteOferta,
        uploadOfertaImagem,
        deleteOfertaImagem,
        setOfertaImagemPrincipal,
        isPending 
    };
};